import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import {
  CalendarDaysIcon,
  GlobeAltIcon,
  ClockIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"
import ManageSubscriptionDialog from "./ManageSubscriptionDialog"
import CancellationFlow from "./CancelFlow"
import {
  priceDisplayFormat,
  getPaymentDuration,
} from "aldoo-ra/CMSModels/StorePrice/display-formatters"

const SubscriptionParam = ({ icon: Icon, label, value }) => {
  return (
    <div className="flex">
      <div className="flex items-center text-gray_1">
        <Icon className="w-5 h-5 mr-2" />

        <Typography
          variant="info_18"
        >
          {label}
        </Typography>
      </div>

      <Typography 
        variant="info_18_accent"
        className="text-text ml-2"
      >
        {value}
      </Typography>
    </div>
  )
}

const getSubscriptionDuration = (days, storeTexts, t) => {
  if (days === 7) {
    return storeTexts?.weekly || "Weekly"
  } else if (days === 30) {
    return storeTexts?.monthly || "Monthly"
  } else if (days === 365) {
    return storeTexts?.yearly || "Yearly"
  } else {
    return t(storeTexts?.days || "{{count}} days", { count: days })
  }
}


const SubscriptionCard = ({ subscription }) => {
  const [isManageModalOpen, setIsManageModalOpen] = useState(false)
  const [isCancelFlowOpen, setIsCancelFlowOpen] = useState(false)

  const texts = TextBundle({ id: "subscription_card" })
  const storeTexts = TextBundle({ id: "aldoo-store" })
  const { template, t } = Localization()

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-success/10 text-success"
      case "canceled":
        return "bg-error/10 text-error"
      case "trial":
        return "bg-info/10 text-info"
      default:
        return "bg-neutral_3/10 text-neutral_3"
    }
  }

  const handleStartCancellation = () => {
    setIsManageModalOpen(false)
    setIsCancelFlowOpen(true)
  }

  const handleCancelFlowClose = () => {
    setIsCancelFlowOpen(false)
  }

  // Helper to format phase interval for display
  const formatInterval = (phase) => {
    if (!phase) return ''
    const count = phase.interval_count || 1
    const interval = phase.interval || 'month'
    const localizedInterval = storeTexts?.[interval] || interval
    return count === 1 ? localizedInterval : `${count} ${localizedInterval}s`
  }

  // Convert subscription duration (in days) to localized interval
  const formatDurationInterval = (days) => {
    let interval = 'day'
    let count = days

    if (days === 7) {
      interval = 'week'
      count = 1
    } else if (days === 30) {
      interval = 'month'
      count = 1
    } else if (days === 365) {
      interval = 'year'
      count = 1
    }

    const localizedInterval = storeTexts?.[interval] || interval
    return count === 1 ? localizedInterval : `${count} ${localizedInterval}s`
  }

  // Calculate current price based on phases
  const getCurrentPrice = () => {
    const priceModel = subscription.price_model
    const phases = priceModel?.phases

    // No phases - use regular price
    if (!phases || phases.length === 0) {
      return {
        amount: subscription.product.amount,
        currency: subscription.product.currency,
        isIntro: false,
        regularPhase: null
      }
    }

    // Find intro and regular phases
    const introPhase = phases.find(p => p.phase_type === 'introductory' && p.enabled !== false)
    const regularPhase = phases.find(p => p.phase_type === 'regular' && p.enabled !== false)

    // If no intro phase, use regular price
    if (!introPhase) {
      return {
        amount: regularPhase?.amount || subscription.product.amount,
        currency: regularPhase?.currency || subscription.product.currency,
        isIntro: false,
        regularPhase: null
      }
    }

    // Calculate if we're still in intro period
    const startDate = new Date(subscription.start_date)
    const now = new Date()
    const daysSinceStart = Math.floor((now - startDate) / (1000 * 60 * 60 * 24))

    // Calculate intro period duration in days
    const introDurationDays = introPhase.interval === 'month'
      ? (introPhase.interval_count || 1) * 30
      : introPhase.interval === 'year'
      ? (introPhase.interval_count || 1) * 365
      : introPhase.interval === 'week'
      ? (introPhase.interval_count || 1) * 7
      : (introPhase.interval_count || 1)

    // If still in intro period, show intro price
    if (daysSinceStart < introDurationDays) {
      return {
        amount: introPhase.amount,
        currency: introPhase.currency,
        isIntro: true,
        regularPhase
      }
    }

    // Intro period over, show regular price
    return {
      amount: regularPhase?.amount || subscription.product.amount,
      currency: regularPhase?.currency || subscription.product.currency,
      isIntro: false,
      regularPhase: null
    }
  }

  // Helper to get appropriate date label based on subscription status
  const getDateLabel = () => {
    // Check both canceled field and status field for robustness
    if (subscription.canceled || subscription.status === "canceled") {
      return texts?.access_ends || "Access Ends"
    }
    if (subscription.expired || subscription.status === "expired") {
      return texts?.expired_on || "Expired On"
    }
    return texts?.renew_date || "Renew Date"
  }

  // Helper to get the appropriate date to display
  const getDisplayDate = () => {
    // For expired subscriptions, use the actual expiration date from platform webhooks
    if (subscription.expired || subscription.status === "expired") {
      // Check Apple metadata
      if (subscription.metadata?.apple_expiration_date) {
        return subscription.metadata.apple_expiration_date
      }
      // Check Google Play metadata
      if (subscription.metadata?.google_play?.expiryTime) {
        return new Date(parseInt(subscription.metadata.google_play.expiryTime))
      }
      // Check Stripe metadata (if backend stores current_period_end)
      if (subscription.metadata?.stripe_current_period_end) {
        return subscription.metadata.stripe_current_period_end
      }
    }

    // For canceled subscriptions, also check for actual expiration
    if (subscription.canceled || subscription.status === "canceled") {
      if (subscription.metadata?.apple_expiration_date) {
        return subscription.metadata.apple_expiration_date
      }
      if (subscription.metadata?.google_play?.expiryTime) {
        return new Date(parseInt(subscription.metadata.google_play.expiryTime))
      }
      if (subscription.metadata?.stripe_current_period_end) {
        return subscription.metadata.stripe_current_period_end
      }
    }

    // Default to the original end_date for active subscriptions
    return subscription.end_date
  }

  const currentPrice = getCurrentPrice()
  return (
    <>
      <div className="account-card shadow-custom_card">
        {/* Header Section */}
        <div className="w-full mb-10">
          {/* Plan Name */}
          <Typography
            variant="h5"
            className="text-primary mt-0 md:mb-7"
          >
            {subscription.product.name}
          </Typography>

          {/* Price */}
          <Typography
            variant="subtitle_32_accent"
            className="text-text my-0">
            {priceDisplayFormat(
              currentPrice.amount,
              currentPrice.currency
            )}

            <span className="text-gray_1 font-normal ml-2">
              / {formatDurationInterval(subscription.duration)}
            </span>
          </Typography>

          {/* Show renewal info if in intro phase */}
          {currentPrice.isIntro && currentPrice.regularPhase && (
            <Typography
              variant="info_14"
              className="text-text_secondary/80 mt-1"
            >
              {template(storeTexts?.product_introductory_renew || "Renews at {{price}} / {{duration}} after promo period", {
                price: priceDisplayFormat(currentPrice.regularPhase.amount, currentPrice.regularPhase.currency),
                duration: formatInterval(currentPrice.regularPhase)
              })}
            </Typography>
          )}
        </div>

        {/* Info */}
        <div className="w-full ">
          <SubscriptionParam
            icon={CalendarDaysIcon}
            label={getDateLabel()}
            value={formatDate(getDisplayDate())}
          />
          <SubscriptionParam
            icon={GlobeAltIcon}
            label={texts?.platform}
            value={subscription.platform}
          />
          <SubscriptionParam
            icon={ClockIcon}
            label={texts?.duration}
            value={getSubscriptionDuration(subscription.duration, storeTexts, t)}
          />
          {subscription.trial_end && (
            <SubscriptionParam
              icon={ExclamationCircleIcon}
              label={texts?.trial_end}
              value={formatDate(subscription.trial_end)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="w-full flex justify-between items-center border-t border-bordered pt-6 mt-8">
          {/* Status */}
          <div
            className={`px-4 py-2 rounded-full ${getStatusColor(
              subscription.status
            )} capitalize`}
          >
            <Typography
              variant="info_18"
              className="my-0"
            >
              {subscription.status}
            </Typography>
          </div>

          {/* Manage Button */}
          <Button
            variant="secondary_s"
            className="w-auto"
            onClick={() => setIsManageModalOpen(true)}
          >
            {texts?.cta}
          </Button>
        </div>
      </div>

      <ManageSubscriptionDialog
        isOpen={isManageModalOpen}
        onClose={() => setIsManageModalOpen(false)}
        onCancel={handleStartCancellation}
        subscription={subscription}
      />

      <CancellationFlow
        isOpen={isCancelFlowOpen}
        onClose={handleCancelFlowClose}
        subscription={subscription}
      />
    </>
  )
}

export default SubscriptionCard
